<template>
  <div class="saas-wrapper">
    <a-spin :spinning="loading" tip="Yükleniyor...">
      <div v-if="!loading && services.length === 0" class="empty-state">
        <CloudUploadOutlined style="font-size: 48px; color: #d9d9d9" />
        <p>Henüz SaaS servisi tanımlanmamış.</p>
      </div>

      <a-row
        v-else
        :gutter="[16, 16]"
        type="flex">
        <a-col
          v-for="service in services"
          :key="service.id"
          :xs="24"
          :sm="12"
          :md="6">
          <component
            :is="isExternal(service) ? 'a' : 'div'"
            :href="isExternal(service) ? externalHref(service.link) : undefined"
            :target="isExternal(service) ? '_blank' : undefined"
            :rel="isExternal(service) ? 'noopener noreferrer' : undefined"
            class="card-link"
            :class="{ 'card-link-disabled': !hasLink(service) }"
            @click="!isExternal(service) && navigateInternal(service.link)">
            <a-card hoverable class="card-item">
              <div class="card-icon-wrapper">
                <img
                  v-if="service.icon"
                  :src="portalUrl + service.icon"
                  :alt="service.name"
                  class="card-logo" />
                <CloudUploadOutlined
                  v-else
                  style="font-size: 32px; color: #1890ff" />
              </div>
              <h3 class="card-title">{{ service.name }}</h3>
              <p class="card-description">{{ service.description || '' }}</p>
            </a-card>
          </component>
        </a-col>
      </a-row>
    </a-spin>
  </div>
</template>

<script>
import { ref, onMounted } from 'vue'
import { CloudUploadOutlined } from '@ant-design/icons-vue'
import { useRouter } from 'vue-router'
import { useStore } from 'vuex'
import axios from 'axios'
import { csPortalUrl } from '@/utils/csPortal'

export default {
  name: 'SaasView',
  components: {
    CloudUploadOutlined
  },
  setup () {
    const router = useRouter()
    const store = useStore()
    const loading = ref(false)
    const services = ref([])
    // cs-portal adresi config.json (csPortalUrl) / Docker CS_PORTAL_URL uzerinden gelir
    const CS_PORTAL_URL = csPortalUrl()

    const fetchServices = async () => {
      loading.value = true
      try {
        const response = await axios.get(`${CS_PORTAL_URL}/api/saas/public`)
        services.value = response.data
      } catch (error) {
        console.error('SaaS servisleri yüklenemedi:', error)
      } finally {
        loading.value = false
      }
    }

    const hasLinkValue = (link) => {
      const v = (link || '').trim()
      return v !== '' && v !== '#'
    }

    // cs-portal'daki linkte oturumdaki kullaniciya gore doldurulan yer tutucular:
    //   {domain}   -> kullanicinin domain adi, hostname etiketi olarak normalize edilir:
    //                 kucuk harf, harf/rakam disi her karakter '-' (itg.org.tr -> itg-org-tr).
    //                 Ornek: https://fw-{domain}.bulutpark.cloud -> https://fw-itg-org-tr.bulutpark.cloud
    //   {account}  -> hesap adi (kucuk harf)
    //   {username} -> kullanici adi (kucuk harf)
    //   {domainid} -> domain UUID
    const hostLabel = (v) => String(v || '')
      .trim()
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '')

    const placeholderValues = () => {
      const info = store.getters.userInfo || {}
      const clean = (v) => String(v || '').trim().toLowerCase()
      return {
        domain: hostLabel(info.domain),
        account: clean(info.account),
        username: clean(info.username),
        domainid: clean(info.domainid)
      }
    }

    const resolveLink = (link) => {
      if (!hasLinkValue(link)) return link
      const values = placeholderValues()
      return link.replace(/\{(domain|account|username|domainid)\}/gi, (_, key) => values[key.toLowerCase()] || '')
    }

    // Link cs-portal'da nasil kayitli olursa olsun ('/buckets', '#/buckets' veya
    // 'https://eski-domain/#/buckets') bu uygulamaya ait bir route ise yalnizca
    // route path'i doner ('/buckets'); domain dikkate alinmaz. cs-ui hangi adreste
    // calisiyorsa link oraya gider. Bilinmeyen path'ler (gercek dis siteler) icin null.
    const appRoutePath = (link) => {
      if (!hasLinkValue(link)) return null
      let path = null
      if (link.startsWith('#/')) {
        path = link.slice(1)
      } else if (link.startsWith('/') && !link.startsWith('//')) {
        path = link
      } else {
        try {
          const url = new URL(link)
          if (url.hash && url.hash.startsWith('#/')) {
            path = url.hash.slice(1)
          }
        } catch {
          return null
        }
      }
      if (!path) return null
      const resolved = router.resolve(path)
      const known = resolved.matched.length > 0 &&
        !resolved.matched.some(r => String(r.path).startsWith('/:catchAll'))
      return known ? path : null
    }

    const hasLink = (service) => hasLinkValue(service.link)

    const isExternal = (service) => hasLink(service) && service.link_type === 'external'

    // Dis link: uygulama route'u ise mevcut origin'e (hash router) cevrilir,
    // gercek dis site ise oldugu gibi kullanilir.
    const externalHref = (rawLink) => {
      const link = resolveLink(rawLink)
      const path = appRoutePath(link)
      if (path) {
        return `${window.location.origin}${window.location.pathname}#${path}`
      }
      return link
    }

    const navigateInternal = (rawLink) => {
      if (!hasLinkValue(rawLink)) return
      const link = resolveLink(rawLink)
      const path = appRoutePath(link)
      if (path) {
        router.push(path)
        return
      }
      try {
        const url = new URL(link)
        router.push(url.hash ? url.hash.replace('#', '') : url.pathname)
      } catch {
        router.push(link)
      }
    }

    onMounted(fetchServices)

    return { services, loading, navigateInternal, hasLink, isExternal, externalHref, portalUrl: CS_PORTAL_URL }
  }
}
</script>

<style scoped>
.saas-wrapper {
  padding: 0;
}
.empty-state {
  text-align: center;
  padding: 60px 20px;
  color: #8c8c8c;
}
.empty-state p {
  margin-top: 12px;
  font-size: 14px;
}
.card-link {
  display: block;
  text-decoration: none;
  color: inherit;
  height: 100%;
  cursor: pointer;
}
.card-link-disabled {
  cursor: default;
  opacity: 0.6;
}
.card-item {
  height: 100%;
}
.card-icon-wrapper {
  width: 52px;
  height: 52px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 16px;
}
.card-logo {
  width: 100%;
  height: 100%;
  object-fit: contain;
}
.card-title {
  font-size: 18px;
  font-weight: 600;
  color: #1a1a2e;
  margin: 0 0 10px 0;
}
.card-description {
  font-size: 13px;
  color: #8c8c8c;
  line-height: 1.6;
  margin: 0;
}
</style>
